/**
 * OMON-151: pure status discriminator for a `_practitionerRole` row.
 *
 * The existing `PractitionerRoleRow.inactive` flag lumps two distinct
 * states together: "admin suspended this role (active=false)" and "role
 * naturally ended (period.end in the past)". For the App Store launch
 * gate the admin UI needs to render these differently — "Suspended"
 * with a re-enable button, "Ended" without — so the discriminator goes
 * here as a pure helper that's trivially unit-testable.
 *
 * Three states:
 *
 *   - `active` — role is in effect. UI renders patient data, audit log
 *     would show normal use.
 *   - `suspended` — `_practitionerRole.active === false`. The iOS app
 *     shows Access Suspended (piece 1). Admin can `resume` to restore.
 *   - `ended` — `period.end` is in the past, `active` is still true.
 *     Used for definitive termination (carer left the company).
 *
 * If both conditions hold (active=false AND period.end past), suspended
 * wins — it's the more actionable status for the admin UI ("you can
 * resume this") even though the practical effect on the user is the
 * same as ended (no app access).
 *
 * Mirrors no server-side counterpart. The CF reads `active` + `period.end`
 * directly; this discriminator lives client-side because it's only the
 * UI that needs to distinguish between them.
 */

/** Three operational states for a `_practitionerRole` row. */
export type PractitionerRoleStatus = "active" | "suspended" | "ended";

/**
 * Compute the operational status from the raw active flag + period.end.
 *
 * @param active - the `_practitionerRole.active` boolean (treat undefined/missing as true; FHIR convention)
 * @param periodEnd - ISO 8601 datetime string, or null if open-ended
 * @param now - injectable clock for unit tests; defaults to Date.now()
 */
export function computeRoleStatus(
  active: boolean,
  periodEnd: string | null,
  now: number = Date.now(),
): PractitionerRoleStatus {
  if (active === false) return "suspended";
  if (periodEnd !== null) {
    const endTime = Date.parse(periodEnd);
    if (!Number.isNaN(endTime) && endTime < now) return "ended";
  }
  return "active";
}

/** Human-readable label for each status. Drives badge rendering. */
export const ROLE_STATUS_LABEL: Record<PractitionerRoleStatus, string> = {
  active: "Active",
  suspended: "Suspended",
  ended: "Ended",
};
